import { useEffect, useState, useRef, useCallback } from "react";
import * as ReactDOMServer from "react-dom/server";
import Spinner from "../Spinner";
import InfoWindow from "./InfoWindow";

export default function VisitsMapComponent(props) {
  const [mapLoading, setMapLoading] = useState(false);
  const [, setGMap] = useState(null);
  const [, setGMarkers] = useState([]);
  const [currentInfoWindow, setCurrentInfoWindow] = useState(null);

  const streetViewRef = useRef(null);

  // Função para criar ícone principal do veículo
  const createMarkerIconMain = () => {
    // Verificar se o DOM está disponível
    if (typeof document === 'undefined') {
      console.error("DOM não está disponível");
      return null;
    }
    
    const icon = document.createElement("img");
    icon.src = "https://cdn4.iconfinder.com/data/icons/twitter-29/512/157_Twitter_Location_Map-1024.png";
    icon.style.width = "55px";
    icon.style.height = "55px";
    icon.style.position = "absolute";
    icon.style.top = "0";
    icon.style.left = "0";
    icon.style.transform = "translate(-40px, -40px)";
    return icon;
  };

  // Função para criar ícone de marcador (baseado no GoogleMapComponent)
  const createMarkerIcon = (iconUrl) => {
    // Verificar se o DOM está disponível
    if (typeof document === 'undefined') {
      console.error("DOM não está disponível");
      return null;
    }
    
    const icon = document.createElement("img");
    icon.src = iconUrl;
    icon.style.width = "100px";
    icon.style.height = "100px";
    icon.style.position = "absolute";
    icon.style.top = "0";
    icon.style.left = "0";
    icon.style.transform = "translate(-40px, -40px)";
    return icon;
  };

  // Conteúdo do InfoWindow (baseado no GoogleMapComponent)
  const getMarkerContent = (data, vehicle) => {
    return (
      <InfoWindow
        data={data}
        id={props.id}
        vehicle={vehicle}
        hideIcons={props.hideIcons}
        activeTab={props.activeTab}
      />
    );
  };

  // Criar marcador de veículo principal (baseado no createMarkerVehicleMain)
  const createMarkerVehicleMain = (data, map) => {
    // Verificar se o Google Maps está carregado
    if (!window.google || !window.google.maps) {
      console.error("Google Maps API não está carregada");
      return null;
    }
    
    const dataLat = data.latitude ?? data[0]?.latitude;
    const dataLng = data.longitude ?? data[0]?.longitude;

    const position = new window.google.maps.LatLng(
      dataLat,
      dataLng
    );

    const dataReady = data ?? data[0];

    const icon = createMarkerIconMain();

    const markerContent = getMarkerContent(dataReady);

    const marker = new window.google.maps.marker.AdvancedMarkerElement({
      position,
      map,
      content: icon,
      title: data.location,
    });

    marker.infoContent = ReactDOMServer.renderToString(markerContent);

    marker.addListener("gmp-click", () => {
      if (currentInfoWindow) currentInfoWindow.close();

      const infoWindow = new window.google.maps.InfoWindow({
        content: marker.infoContent,
        ariaLabel: "Street View",
      });

      infoWindow.open({ anchor: marker, map });
      setCurrentInfoWindow(infoWindow);

      setTimeout(() => {
        let streetView = new window.google.maps.StreetViewPanorama(
          streetViewRef.current,
          {
            position: marker.position,
            pov: { heading: 34, pitch: 10 },
            zoom: 1,
          }
        );

        const interval = setInterval(() => {
          const svContainer = document.getElementById("street-view-preview");
          if (svContainer) {
            svContainer.style.cursor = "pointer";

            streetView = new window.google.maps.StreetViewPanorama(
              svContainer,
              {
                position: marker.position,
                pov: { heading: 34, pitch: 10 },
                zoom: 1,
                disableDefaultUI: true,
                showRoadLabels: false,
              }
            );

            svContainer.addEventListener("dblclick", () => {
              const currentPosition = marker.position;
              const lat =
                typeof currentPosition.lat === "function"
                  ? currentPosition.lat()
                  : currentPosition.lat;
              const lng =
                typeof currentPosition.lng === "function"
                  ? currentPosition.lng()
                  : currentPosition.lng;
              const url = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}&heading=34&pitch=10&zoom=1`;
              window.open(url, "_blank");
            });

            clearInterval(interval);
          }
        }, 100);

        map.setStreetView(streetView);
      }, 200);
    });

    return marker;
  };

  // Criar marcador normal (baseado no createMarker do GoogleMapComponent)
  const createMarker = (data, map) => {
    // Verificar se o Google Maps está carregado
    if (!window.google || !window.google.maps) {
      console.error("Google Maps API não está carregada");
      return null;
    }
    
    const position = new window.google.maps.LatLng(
      data.latitude,
      data.longitude
    );
    const icon = createMarkerIcon(data.icon);
    const markerContent = getMarkerContent(data);

    const marker = new window.google.maps.marker.AdvancedMarkerElement({
      position,
      map,
      content: icon,
      title: data.location,
    });

    marker.vehicleId = data.vehicleId; // custom ID
    marker.infoContent = ReactDOMServer.renderToString(markerContent);

    marker.addListener("gmp-click", () => {
      if (currentInfoWindow) currentInfoWindow.close();

      const infoWindow = new window.google.maps.InfoWindow({
        content: marker.infoContent,
        ariaLabel: "Street View",
      });

      infoWindow.open({ anchor: marker, map });
      setCurrentInfoWindow(infoWindow);

      setTimeout(() => {
        let streetView = new window.google.maps.StreetViewPanorama(
          streetViewRef.current,
          {
            position: marker.position,
            pov: { heading: 34, pitch: 10 },
            zoom: 1,
          }
        );

        const interval = setInterval(() => {
          const svContainer = document.getElementById("street-view-preview");
          if (svContainer) {
            svContainer.style.cursor = "pointer";

            streetView = new window.google.maps.StreetViewPanorama(
              svContainer,
              {
                position: marker.position,
                pov: { heading: 34, pitch: 10 },
                zoom: 1,
                disableDefaultUI: true,
                showRoadLabels: false,
              }
            );

            svContainer.addEventListener("dblclick", () => {
              const currentPosition = marker.position;
              const lat =
                typeof currentPosition.lat === "function"
                  ? currentPosition.lat()
                  : currentPosition.lat;
              const lng =
                typeof currentPosition.lng === "function"
                  ? currentPosition.lng()
                  : currentPosition.lng;
              const url = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}&heading=34&pitch=10&zoom=1`;
              window.open(url, "_blank");
            });

            clearInterval(interval);
          }
        }, 100);

        map.setStreetView(streetView);
      }, 200);
    });

    return marker;
  };

  // Inicializar mapa
  const addMap = useCallback(() => {
    // Verificar se o Google Maps está carregado
    if (!window.google || !window.google.maps) {
      console.error("Google Maps API não está carregada");
      return;
    }

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: "Brazil" }, function (results, status) {
      if (status === window.google.maps.GeocoderStatus.OK) {
        const map = new window.google.maps.Map(document.getElementById("visits-map"), {
          center: results[0].geometry.location,
          zoom: 14,
          streetViewControl: false,
          mapId: "visits-map",
        });

        // Adicionar marcadores de veículos próximos
        if (props.locations?.length > 0) {
          addMarkers(map, props.locations);
        }

        setGMap(map);
      } else {
        console.error("Erro ao geocodificar Brasil:", status);
      }
    });
  }, [props.locations]);

  // Adicionar marcadores (baseado no addMarkers do GoogleMapComponent)
  const addMarkers = (map, locations) => {
    // Verificar se o Google Maps está carregado
    if (!window.google || !window.google.maps) {
      console.error("Google Maps API não está carregada");
      return;
    }
    
    const markers = [];
    const markerMainObj = [];
    const bounds = new window.google.maps.LatLngBounds();

    for (let i = 0; i < locations?.length; i++) {
      const data = locations[i];

      if (data.latitude && data.longitude && data.latitude !== data.longitude) {
        // Adicionar marcador principal do veículo se for mapVisits
        if (props.activeTab === "mapVisits") {
          const markerMain = createMarkerVehicleMain(props.dataVehicle, map);
          markerMainObj.push(markerMain);
        }

        // Adicionar marcador normal
        const marker = createMarker(data, map);
        markers.push(marker);

        const position = new window.google.maps.LatLng(
          data.latitude,
          data.longitude
        );

        bounds.extend(position);
      }
    }

    map.fitBounds(bounds);

    setGMarkers(markers);
    if (props.activeTab === "mapVisits") {
      setGMarkers(markerMainObj);
    }
  };

  // Effect para inicializar o mapa
  useEffect(() => {
    if (props.activeTab === "mapVisits") {
      setMapLoading(props.mapLoading || false);

      if (props.createMap) {
        // Verificar se o Google Maps está carregado antes de inicializar
        if (window.google && window.google.maps) {
          console.log("Inicializando mapa de visitas");
          addMap();
        } else {
          console.log("Aguardando Google Maps API carregar...");
          // Aguardar um pouco e tentar novamente
          const timer = setTimeout(() => {
            if (window.google && window.google.maps) {
              console.log("Google Maps API carregada, inicializando mapa");
              addMap();
            } else {
              console.error("Google Maps API não carregou após timeout");
            }
          }, 1000);
          
          return () => clearTimeout(timer);
        }
      }
    }
  }, [props.activeTab, props.createMap, props.locations, props.dataVehicle, addMap]);

  return (
    <>
      {mapLoading && <Spinner />}
      
      <div style={{ width: "100%", height: "100%", position: "relative" }}>
        <div
          id="visits-map"
          style={{ 
            width: "100%", 
            height: "100%", 
            backgroundColor: "#f5f5f5" 
          }}
        />
      </div>
    </>
  );
}
